export type Lang = "en" | "es";

/**
 * A value in site.json is either shared by both languages (a plain value) or a
 * translation pair: an object whose keys are exactly `en` and `es`.
 *
 * The runtime guard requires exactly two keys; the `Localized` type is looser
 * (it matches any object carrying `en` and `es`). site.parity.test.ts closes
 * that gap by asserting no object in the data mixes `en`/`es` with other keys.
 */
export type Localized<T> =
  T extends { en: infer E; es: unknown } ? E :
  T extends readonly (infer U)[] ? Localized<U>[] :
  T extends object ? { [K in keyof T]: Localized<T[K]> } :
  T;

export const isTranslationPair = (node: unknown): node is Record<Lang, unknown> => {
  if (!node || typeof node !== "object" || Array.isArray(node)) return false;
  const keys = Object.keys(node);
  return keys.length === 2 && keys.indexOf("en") !== -1 && keys.indexOf("es") !== -1;
};

// Object.keys only — the project targets es2016, so Object.entries is unavailable.
const walk = (node: unknown, lang: Lang): unknown => {
  if (isTranslationPair(node)) return walk(node[lang], lang);
  if (Array.isArray(node)) return node.map((item) => walk(item, lang));
  if (node && typeof node === "object") {
    const source = node as Record<string, unknown>;
    const resolved: Record<string, unknown> = {};
    Object.keys(source).forEach((key) => {
      resolved[key] = walk(source[key], lang);
    });
    return resolved;
  }
  return node;
};

export const resolveContent = <T>(node: T, lang: Lang): Localized<T> =>
  walk(node, lang) as Localized<T>;
